import { useMemo, useState } from "react"
import { MdCalendarMonth, MdChevronLeft, MdChevronRight, MdClose } from "react-icons/md"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiGestor } from "../../services/api"
import { useAuth } from "../../context/AuthContext"

interface Seller {
    id_usuario?: string;
    usuario?: string;
    id_vendedor?: string;
    nome_vendedor?: string;
}

interface FolgaRegistro {
    idVendedorFolga: number;
    idVendedor: number;
    nomeVendedor: string;
    codEmpresaGazin: number;
    dataFolga: string;
    idUsuario?: number;
    dataHora?: string | null;
    status: boolean | string;
}

export function Cadastro() {
    const { decodedToken, cookies } = useAuth();
    const decoded = decodedToken ? decodedToken() : null;
    console.log(decoded)
    const idLoja = decoded?.id_loja || "1";

    const queryClient = useQueryClient();

    const [selectedSeller, setSelectedSeller] = useState<Seller | null>(null)
    const [currentMonth, setCurrentMonth] = useState(new Date())
    const [searchTerm, setSearchTerm] = useState("")

    const [datesToRegister, setDatesToRegister] = useState<string[]>([])
    const [folgasToCancel, setFolgasToCancel] = useState<number[]>([])

    // Query para Vendedores
    const { data: sellersData, isLoading: isLoadingSellers } = useQuery({
        queryKey: ['vendedores', idLoja],
        queryFn: async () => {
            const res = await apiGestor.get(`/aciona-one/dashboard/vendedor/${idLoja}`);
            return res.data;
        },
        enabled: !!idLoja,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });

    // Query para Folgas
    const { data: folgasData, isLoading: isLoadingFolgas } = useQuery({
        queryKey: ['folgas', idLoja, currentMonth.getFullYear(), currentMonth.getMonth()],
        queryFn: async () => {
            const year = currentMonth.getFullYear()
            const month = currentMonth.getMonth()
            const lastDay = new Date(year, month + 1, 0).getDate()

            const dataInicio = `${year}-${String(month + 1).padStart(2, "0")}-01`
            const dataFim = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`

            const res = await apiGestor.get('/v1/folgas', {
                params: {
                    codEmpresaGazin: idLoja,
                    pageSize: 1000,
                    dataInicio,
                    dataFim
                }
            });
            return res.data;
        },
        enabled: !!idLoja,
        refetchOnWindowFocus: false,
    });

    // Submit Changes
    const submitChangesMutation = useMutation({
        mutationFn: async () => {
            const promises: Promise<any>[] = [];

            for (const date of datesToRegister) {
                promises.push(apiGestor.post('/v1/folgas', {
                    idVendedor: String(selectedSeller?.id_usuario || selectedSeller?.id_vendedor),
                    nomeVendedor: selectedSeller?.usuario || selectedSeller?.nome_vendedor,
                    codEmpresaGazin: Number(idLoja),
                    idUsuario: String(decoded?.id_usuario),
                    dataFolga: date
                }));
            }

            for (const folgaId of folgasToCancel) {
                promises.push(apiGestor.patch(`/v1/folgas/${folgaId}/status`, { status: false }));
            }

            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['folgas', idLoja] });
            setDatesToRegister([]);
            setFolgasToCancel([]);
        }
    });

    const handleSelectSeller = (seller: Seller | null) => {
        setSelectedSeller(seller);
        setDatesToRegister([]);
        setFolgasToCancel([]);
    }

    const handleCancelSelection = () => {
        setDatesToRegister([]);
        setFolgasToCancel([]);
    }

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear()
        const month = date.getMonth()
        const firstDay = new Date(year, month, 1)
        const lastDay = new Date(year, month + 1, 0)
        const daysInMonth = lastDay.getDate()
        const startingDayOfWeek = firstDay.getDay()

        return { daysInMonth, startingDayOfWeek, year, month }
    }

    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth)

    const monthNames = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ]

    const sellersList: Seller[] = useMemo(() => {
        let arr = sellersData || [];
        if (sellersData?.data) arr = sellersData.data;
        if (!Array.isArray(arr)) arr = [];
        return arr;
    }, [sellersData]);

    const folgasList: FolgaRegistro[] = useMemo(() => {
        let arr = folgasData?.registros || folgasData?.data?.registros || [];
        if (!Array.isArray(arr)) arr = [];
        return arr.filter((f: any) => f.status === true || f.status === 'ATIVO');
    }, [folgasData]);

    const getFolgaForDate = (day: number) => {
        if (!selectedSeller) return null;
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`

        return folgasList.find(f =>
            String(f.idVendedor) === String(selectedSeller.id_usuario || selectedSeller.id_vendedor) &&
            f.dataFolga.startsWith(dateStr)
        );
    }

    const toggleTimeOff = (day: number) => {
        if (!selectedSeller || submitChangesMutation.isLoading) return
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`

        const existingFolga = getFolgaForDate(day);

        if (existingFolga) {
            // Already a folga, toggle cancel state
            if (folgasToCancel.includes(existingFolga.idVendedorFolga)) {
                setFolgasToCancel(prev => prev.filter(id => id !== existingFolga.idVendedorFolga));
            } else {
                setFolgasToCancel(prev => [...prev, existingFolga.idVendedorFolga]);
            }
        } else {
            // New folga, toggle register state
            if (datesToRegister.includes(dateStr)) {
                setDatesToRegister(prev => prev.filter(d => d !== dateStr));
            } else {
                setDatesToRegister(prev => [...prev, dateStr]);
            }
        }
    }

    const today = new Date()
    const isCurrentMonth = currentMonth.getMonth() === today.getMonth() && currentMonth.getFullYear() === today.getFullYear()
    const isPastMonth = currentMonth < new Date(today.getFullYear(), today.getMonth(), 1)

    const goToPreviousMonth = () => {
        if (!isCurrentMonth && !isPastMonth) {
            setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
        }
    }

    const goToNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
    }

    const filteredSellers = sellersList.filter((seller) => {
        const name = seller.usuario || seller.nome_vendedor || "";
        return name.toLowerCase().includes(searchTerm.toLowerCase());
    })

    const getTotalFolgas = (sellerId: string) => {
        return folgasList.filter(f => String(f.idVendedor) === String(sellerId)).length;
    }

    const hasPendingChanges = datesToRegister.length > 0 || folgasToCancel.length > 0;

    return (
        <section>
            <div className="flex h-sideBar w-full mt-4 p-6 flex-col bg-[#f1f5f9]">
                <div className="flex items-center gap-3 mb-6">
                    <MdCalendarMonth className="w-8 h-8 text-primary" />
                    <h2 className="text-2xl font-bold text-gray-800">Gestão de Folgas</h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Lista de Vendedores */}
                    <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-4">
                        <h2 className="text-lg font-semibold text-gray-700 mb-4">Vendedores</h2>

                        <input
                            type="text"
                            placeholder="Buscar vendedor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-1 focus:ring-primary"
                        />

                        {isLoadingSellers || isLoadingFolgas ? (
                            <div className="p-4 text-center text-gray-500">Carregando dados...</div>
                        ) : (
                            <div className="space-y-2 max-h-[600px] overflow-y-auto">
                                {filteredSellers.map((seller) => (
                                    <button
                                        key={seller.id_usuario || seller.id_vendedor}
                                        onClick={() => handleSelectSeller(seller)}
                                        className={`w-full text-left px-4 py-3 rounded-lg transition-all ${(selectedSeller?.id_usuario || selectedSeller?.id_vendedor) === (seller.id_usuario || seller.id_vendedor)
                                            ? "bg-primary text-white"
                                            : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                                            }`}
                                    >
                                        <div className="font-medium text-sm">{seller.usuario || seller.nome_vendedor}</div>
                                        <div className="text-xs mt-1 opacity-75">{getTotalFolgas(String(seller.id_usuario || seller.id_vendedor))} folga(s) agendada(s)</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Calendário */}
                    <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6 relative flex flex-col justify-between overflow-y-auto max-h-[calc(100vh-130px)]">
                        {submitChangesMutation.isLoading && (
                            <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10 rounded-lg">
                                <span className="text-lg font-semibold text-primary">Salvando...</span>
                            </div>
                        )}

                        {selectedSeller ? (
                            <>
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-800">{selectedSeller.usuario || selectedSeller.nome_vendedor}</h2>
                                        <p className="text-sm text-gray-500 mt-1">Selecione os dias de folga no calendário</p>
                                    </div>
                                    <button onClick={() => handleSelectSeller(null)} className="p-2 rounded-lg hover:bg-gray-100">
                                        <MdClose className="w-5 h-5 text-gray-500" />
                                    </button>
                                </div>

                                <div className="mb-6 flex-1 flex flex-col items-center">
                                    <div className="flex items-center justify-between w-full max-w-lg mb-4">
                                        <button
                                            onClick={goToPreviousMonth}
                                            disabled={isCurrentMonth || isPastMonth}
                                            className={`p-2 rounded-lg ${isCurrentMonth || isPastMonth ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}`}
                                        >
                                            <MdChevronLeft className="w-6 h-6" />
                                        </button>
                                        <h3 className="text-lg font-semibold text-gray-700">
                                            {monthNames[month]} {year}
                                        </h3>
                                        <button onClick={goToNextMonth} className="p-2 rounded-lg hover:bg-gray-100">
                                            <MdChevronRight className="w-6 h-6" />
                                        </button>
                                    </div>

                                    {/* Componente do Calendário */}
                                    <div className="grid grid-cols-7 gap-1 sm:gap-2 w-full max-w-lg">
                                        {/* Cabeçalho da Semana */}
                                        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day) => (
                                            <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                                                {day}
                                            </div>
                                        ))}

                                        {/* Espaços Vazios Início */}
                                        {Array.from({ length: startingDayOfWeek }).map((_, i) => (
                                            <div key={`empty-${i}`} className="aspect-square" />
                                        ))}

                                        {/* Dias Normais */}
                                        {Array.from({ length: daysInMonth }).map((_, i) => {
                                            const day = i + 1
                                            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`

                                            const folgaObj = getFolgaForDate(day)

                                            // Status local
                                            const isRegistered = !!folgaObj && !folgasToCancel.includes(folgaObj.idVendedorFolga);
                                            const isMarkedForCancel = !!folgaObj && folgasToCancel.includes(folgaObj.idVendedorFolga);
                                            const isMarkedForRegister = datesToRegister.includes(dateStr);

                                            const todayDate = new Date()
                                            todayDate.setHours(0, 0, 0, 0)
                                            const currentDate = new Date(year, month, day)
                                            const isToday =
                                                todayDate.getDate() === day && todayDate.getMonth() === month && todayDate.getFullYear() === year
                                            const isPastDate = currentDate < todayDate
                                            const isDisabled = isPastDate || isToday || submitChangesMutation.isLoading

                                            const getTooltip = () => {
                                                if (isMarkedForRegister) return "Marcado para adicionar"
                                                if (isMarkedForCancel) return "Marcado para cancelar"
                                                if (isRegistered) return "Folga Registrada (Clique para cancelar)"
                                                if (isToday) return "Não é permitido registrar no dia atual"
                                                if (isPastDate) return "Não é permitido registrar nesse dia"
                                                return "Clique para marcar folga"
                                            }

                                            // Determine visual styling
                                            let dayClass = "bg-gray-50 text-gray-700 hover:bg-gray-100";
                                            if (isDisabled && !folgaObj && !isMarkedForRegister) {
                                                dayClass = "bg-gray-100 text-gray-400 cursor-not-allowed";
                                            } else if (isRegistered) {
                                                dayClass = "bg-red-500 text-white hover:bg-red-600 shadow";
                                            } else if (isMarkedForRegister) {
                                                dayClass = "bg-blue-500 text-white hover:bg-blue-600 shadow border-2 border-blue-400";
                                            } else if (isMarkedForCancel) {
                                                dayClass = "bg-red-200 text-red-800 hover:bg-red-300 line-through opacity-80 border-2 border-red-500";
                                            }

                                            return (
                                                <button
                                                    key={day}
                                                    onClick={() => !isDisabled && toggleTimeOff(day)}
                                                    disabled={isDisabled}
                                                    title={getTooltip()}
                                                    className={`w-full h-10 sm:h-12 lg:h-14 rounded-lg flex items-center justify-center text-sm font-medium transition-all ${dayClass}`}
                                                >
                                                    {day}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>

                                <div className="flex items-center justify-between pt-4 border-t mt-auto">
                                    <div className="text-sm text-gray-600">
                                        <span className="font-semibold text-primary">{getTotalFolgas(String(selectedSeller.id_usuario || selectedSeller.id_vendedor))}</span> dia(s) de folga cadastrado(s)
                                    </div>
                                    <div className="flex gap-3">
                                        {hasPendingChanges && (
                                            <>
                                                <button
                                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-lg transition-colors"
                                                    onClick={handleCancelSelection}
                                                    disabled={submitChangesMutation.isLoading}
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors shadow"
                                                    onClick={() => submitChangesMutation.mutate()}
                                                    disabled={submitChangesMutation.isLoading}
                                                >
                                                    Enviar
                                                </button>
                                            </>
                                        )}
                                        {!hasPendingChanges && (
                                            <button
                                                className="bg-primary hover:opacity-90 text-white px-4 py-2 rounded-lg"
                                                onClick={() => handleSelectSeller(null)}
                                            >
                                                Concluir
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-gray-400">
                                <MdCalendarMonth className="w-16 h-16 mb-4 opacity-50" />
                                <p className="text-lg font-medium">Selecione um vendedor</p>
                                <p className="text-sm">Escolha um vendedor da lista para gerenciar suas folgas</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}
